import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "@/lib/auth";
import { registerBntAsistenteIntegratedOpener } from "@/modules/asistente/bntAsistenteRoutes";
import { useBntAsistenteSummary } from "@/modules/asistente/useBntAsistenteSummary";
import {
  BNT_ASISTENTE_CHANNELS,
  ackBntAsistenteChannels,
  channelsForTab,
  computeBntAsistenteBadgeTotal,
  detectBntAsistenteIncreasedChannels,
  detectBntAsistenteUnreadChannels,
  pickBntAsistenteTabForSummary,
  readBntAsistenteAutoOpen,
  readBntAsistenteLastAck,
  unreadTabsFromChannels,
  writeBntAsistenteAutoOpen,
  writeBntAsistenteLastAck,
} from "@/modules/asistente/bntAsistenteStorage";
import { isBntAsistenteFloatingUiEnabled } from "@/modules/asistente/bntAsistenteFeatureFlags";

/** closed | open | minimized */
const BntAsistenteContext = createContext(null);

const EMPTY_CHAT_FOCUS = { recipientId: "", pulsePrivate: false };

export function BntAsistenteProvider({ children, floatingUiEnabled = isBntAsistenteFloatingUiEnabled() }) {
  const { user } = useAuth();
  // Arranca minimizado (dock visible) para que el asistente quede activo; no se cierra del todo.
  const [panelState, setPanelState] = useState(() => (floatingUiEnabled ? "minimized" : "closed"));
  const [activeTab, setActiveTab] = useState("interno");
  const [autoOpenEnabled, setAutoOpenEnabled] = useState(
    () => floatingUiEnabled && readBntAsistenteAutoOpen(),
  );
  const [unreadTabs, setUnreadTabs] = useState({});
  const [chatPrivateFocus, setChatPrivateFocus] = useState(EMPTY_CHAT_FOCUS);
  const autoOpenedRef = useRef(false);
  const lastAckRef = useRef(readBntAsistenteLastAck());
  const prevSummaryRef = useRef(null);

  const pollMs = panelState === "open" ? 45000 : 15000;

  const { summary, loading, reload } = useBntAsistenteSummary({
    enabled: Boolean(user),
    pollMs: floatingUiEnabled ? pollMs : 45000,
  });

  const badgeTotal = useMemo(() => computeBntAsistenteBadgeTotal(summary), [summary]);

  const acknowledgeNotifications = useCallback(() => {
    writeBntAsistenteLastAck(summary);
    lastAckRef.current = readBntAsistenteLastAck();
    setUnreadTabs({});
    setChatPrivateFocus(EMPTY_CHAT_FOCUS);
  }, [summary]);

  const acknowledgeTab = useCallback((tab) => {
    const channels = channelsForTab(tab);
    if (!channels.length) return;
    const next = ackBntAsistenteChannels(summary, channels, lastAckRef.current);
    lastAckRef.current = next;
    setUnreadTabs((prev) => {
      if (!prev[tab]) return prev;
      const nextTabs = { ...prev };
      delete nextTabs[tab];
      return nextTabs;
    });
    if (tab === "interno") {
      setChatPrivateFocus((prev) => ({ ...prev, pulsePrivate: false }));
    }
  }, [summary]);

  const setActiveTabWithAck = useCallback((tab) => {
    setActiveTab(tab);
    acknowledgeTab(tab);
  }, [acknowledgeTab]);

  const openPanel = useCallback(({ tab, acknowledge = false } = {}) => {
    if (tab) {
      setActiveTab(tab);
      if (acknowledge) {
        window.setTimeout(() => acknowledgeTab(tab), 0);
      }
    }
    setPanelState("open");
  }, [acknowledgeTab]);

  const minimizePanel = useCallback(() => {
    setPanelState("minimized");
  }, []);

  /** Alias: el asistente no se cierra; solo se minimiza a la barra de tareas. */
  const closePanel = minimizePanel;

  const togglePanel = useCallback(({ tab } = {}) => {
    if (panelState === "open") {
      minimizePanel();
      return;
    }
    openPanel({ tab: tab || activeTab });
  }, [activeTab, minimizePanel, openPanel, panelState]);

  const setAutoOpen = useCallback((enabled) => {
    setAutoOpenEnabled(Boolean(enabled));
    writeBntAsistenteAutoOpen(Boolean(enabled));
  }, []);

  useEffect(() => {
    registerBntAsistenteIntegratedOpener(({ tab } = {}) => {
      openPanel({ tab });
    });
    return () => registerBntAsistenteIntegratedOpener(null);
  }, [openPanel]);

  useEffect(() => {
    if (!floatingUiEnabled || !user) return;

    const prevSummary = prevSummaryRef.current;
    const unreadChannels = detectBntAsistenteUnreadChannels(summary, lastAckRef.current);
    const increasedChannels = prevSummary
      ? detectBntAsistenteIncreasedChannels(summary, prevSummary)
      : unreadChannels;

    if (unreadChannels.length) {
      setUnreadTabs(unreadTabsFromChannels(unreadChannels));
      if (
        unreadChannels.includes(BNT_ASISTENTE_CHANNELS.CHAT_PRIVATE)
        && summary.latestPrivateSenderId
      ) {
        setChatPrivateFocus({
          recipientId: summary.latestPrivateSenderId,
          pulsePrivate: true,
        });
      }
    } else {
      setUnreadTabs({});
      setChatPrivateFocus(EMPTY_CHAT_FOCUS);
    }

    if (increasedChannels.length && badgeTotal > 0) {
      const tab = pickBntAsistenteTabForSummary(summary, lastAckRef.current, increasedChannels);
      if (autoOpenEnabled && panelState !== "open") {
        setActiveTab(tab);
        setPanelState("open");
        autoOpenedRef.current = true;
        if (increasedChannels.includes(BNT_ASISTENTE_CHANNELS.CHAT_PRIVATE) && summary.latestPrivateSenderId) {
          setChatPrivateFocus({
            recipientId: summary.latestPrivateSenderId,
            pulsePrivate: true,
          });
        }
      }
    }

    prevSummaryRef.current = summary;
  }, [
    autoOpenEnabled,
    badgeTotal,
    floatingUiEnabled,
    panelState,
    summary,
    user,
  ]);

  const value = useMemo(
    () => ({
      panelState,
      activeTab,
      setActiveTab: setActiveTabWithAck,
      autoOpenEnabled,
      setAutoOpen,
      summary,
      loading,
      reload,
      badgeTotal,
      unreadTabs,
      chatPrivateFocus,
      openPanel,
      minimizePanel,
      closePanel,
      togglePanel,
      acknowledgeNotifications,
      acknowledgeTab,
      autoOpenedRef,
    }),
    [
      activeTab,
      autoOpenEnabled,
      badgeTotal,
      acknowledgeNotifications,
      acknowledgeTab,
      chatPrivateFocus,
      closePanel,
      loading,
      minimizePanel,
      openPanel,
      panelState,
      reload,
      setActiveTabWithAck,
      setAutoOpen,
      summary,
      togglePanel,
      unreadTabs,
    ],
  );

  return (
    <BntAsistenteContext.Provider value={value}>
      {children}
    </BntAsistenteContext.Provider>
  );
}

export function useBntAsistente() {
  const ctx = useContext(BntAsistenteContext);
  if (!ctx) {
    throw new Error("useBntAsistente debe usarse dentro de BntAsistenteProvider");
  }
  return ctx;
}

export function useBntAsistenteOptional() {
  return useContext(BntAsistenteContext);
}
